<template>
  <div class="tracking-network-form">
    <form @submit.prevent="submit">
      <magic-container>
        <v-text-field
          autofocus
          v-model="name"
          :label="$t('network_name')"
          type="text"
          :error-messages="nameErrors"
          @blur="v$.name.$touch()"
        />
        <v-select
          v-model="networkType"
          :label="$t('traffic_network_type')"
          :items="networkTypes"
          item-title="name"
          item-value="id"
          :hide-details="networkTypeErrors.length === 0"
          :error-messages="networkTypeErrors"
          @blur="v$.networkType.$touch()"
        />
      </magic-container>

      <v-textarea
        v-model="trackingUrl"
        :label="$t('tracking_url')"
        auto-grow
        variant="outlined"
        density="compact"
        rows="1"
        :error-messages="trackingUrlErrors"
        :hide-details="false"
        @keydown="preventEnter"
        @blur="v$.trackingUrl.$touch()"
      />

      <Can
        action="see-has-api-checkbox"
        subject="clicky-networks"
      >
        <v-textarea
          v-model="apiBaseUrl"
          :label="$t('api_base_url')"
          auto-grow
          variant="outlined"
          density="compact"
          rows="1"
          @keydown="preventEnter"
        />

        <v-checkbox
          v-model="hasApiIntegration"
          style='margin-top: 0; padding-top: 0'
        >
          <template #label>
            Has API integration
          </template>
        </v-checkbox>
      </Can>

      <div class="form-actions d-flex justify-end">
        <v-btn
          variant="text"
          @click="cancel"
        >
          {{ $t('cancel') }}
        </v-btn>

        <v-btn
          color="primary"
          :loading="isLoading"
          @click="submit"
        >
          {{ $t('add_network_tracking') }}
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core';
  import { required } from '@vuelidate/validators';

  export default {
    emits: ['saved'],
    setup() {
      return { v$: useVuelidate() };
    },
    validations() {
      return {
        name: { required },
        networkType: { required },
        trackingUrl: { required },
      };
    },
    props: {
      onCancel: {
        type: Function,
        default: undefined,
      },
    },
    data() {
      return {
        name: '',
        networkType: null,
        trackingUrl: '',
        apiBaseUrl: '',
        isLoading: false,
        hasApiIntegration: false,
      };
    },
    created() {
      this.$store.dispatch('tracking/getTrafficNetworkTypes');
    },
    computed: {
      networkTypes() {
        return this.$store.getters['tracking/trafficNetworkTypes'];
      },
      nameErrors() {
        return this.v$.name.$errors.map(error => {
          if (error.$validator === 'required') return this.$t('name_is_required');
          return error.$message;
        });
      },
      networkTypeErrors() {
        return this.v$.networkType.$errors.map(error => {
          if (error.$validator === 'required') return this.$t('network_type_is_required');
          return error.$message;
        });
      },
      trackingUrlErrors() {
        return this.v$.trackingUrl.$errors.map(error => {
          if (error.$validator === 'required') return this.$t('tracking_url_is_required');
          return error.$message;
        });
      },
      canSubmitRequest() {
        const { name, networkType, trackingUrl, isLoading } = this;
        return name && networkType !== null && trackingUrl && !isLoading;
      },
    },
    methods: {
      preventEnter(e) {
        if (e?.key === 'Enter') {
          e.preventDefault();
        }
      },
      cancel() {
        this.clear();
        this.onCancel();
      },
      submit() {
        this.v$.$touch();

        if (!this.canSubmitRequest || this.v$.$error || this.v$.$pending) {
          return false;
        }

        this.isLoading = true;

        return this.$store
          .dispatch('tracking/createTrafficNetwork', {
            name: this.name,
            type: this.networkType,
            tracking_link: this.trackingUrl,
            ...(this.$ability.can('see-has-api-checkbox', 'clicky-networks') ? {
              api_base_url: this.apiBaseUrl,
              has_api: this.hasApiIntegration,
            } : {}),
          })
          .then((res) => {
            this.clear();
            this.$emit('saved');
            this.$router.push({
              name: 'tracking-networks',
              query: this.$route.query,
            });
            return res;
          })
          .catch((err) => err)
          .finally(() => {
            this.isLoading = false;
          });
      },
      clear() {
        this.v$.$reset();
        this.name = '';
        this.networkType = null;
        this.trackingUrl = '';
        this.apiBaseUrl = '';
        this.isLoading = false;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

.tracking-network-form {
  background-color: rgb(var(--v-theme-surface));
  padding: $spacing-xl;
}
</style>
